import { CancelOutlined } from '@mui/icons-material';
import { Box, Button, SxProps, Theme, Typography, alpha } from '@mui/material';
import { ReactNode, useCallback } from 'react';

interface DialogContentProps {
    onClose?: () => void;
    children: ReactNode;
    title?: string;
    buttons?: ReactNode | null;
    contentSx?: SxProps<Theme>;
    titleSx?: SxProps<Theme>;
    scrollable?: boolean;
}

export const DialogBoxContent = ({
    onClose,
    children,
    title = '',
    buttons = null,
    contentSx,
    titleSx,
    scrollable = false,
}: DialogContentProps) => {
    const onCloseDialog = useCallback(() => {
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    return (
        <Box display="flex" justifyContent="center" alignItems="center">
            <Box
                minWidth="440px"
                bgcolor="background.paper"
                width="100%"
                sx={
                    scrollable
                        ? {
                              display: 'flex',
                              flexDirection: 'column',
                              maxHeight: 'calc(100vh - 64px)',
                              overflow: 'hidden',
                          }
                        : undefined
                }
            >
                <Box
                    p="16px"
                    pt={title ? 0 : '16px'}
                    sx={
                        scrollable
                            ? {
                                  display: 'flex',
                                  flexDirection: 'column',
                                  flex: 1,
                                  minHeight: 0,
                                  overflow: 'hidden',
                              }
                            : undefined
                    }
                >
                    <Box
                        position="sticky"
                        top={title ? 0 : '16px'}
                        pt={title ? '24px' : 0}
                        display="flex"
                        alignItems="center"
                        justifyContent={title ? 'space-between' : 'flex-end'}
                        mb={title ? '24px' : '0'}
                        sx={titleSx}
                        bgcolor={title ? 'background.paper' : 'transparent'}
                        zIndex={1}
                    >
                        {title && (
                            <Typography
                                typography="openSans.h2"
                                fontSize="18px"
                                lineHeight="20px"
                                fontWeight="700"
                                pb="8px"
                            >
                                {title}
                            </Typography>
                        )}

                        <Button
                            data-testid="btn-close-modal"
                            onClick={onCloseDialog}
                            sx={{ padding: 0, minWidth: 'auto' }}
                        >
                            <CancelOutlined
                                sx={(theme) => ({
                                    color: alpha(
                                        theme.palette.common.black,
                                        0.5,
                                    ),
                                })}
                            />
                        </Button>
                    </Box>
                    <Box
                        p="0 24px"
                        sx={[
                            scrollable && {
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                scrollbarGutter: 'stable',
                            },
                            ...(Array.isArray(contentSx)
                                ? contentSx
                                : contentSx
                                ? [contentSx]
                                : []),
                        ]}
                    >
                        {children}
                    </Box>
                </Box>
                {buttons && (
                    <Box position="sticky" bottom="0px">
                        <Box display="flex" p="14px 30px" bgcolor="grey.200">
                            {buttons}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
